'use client';

import { ExpectedError } from '@/modules/shared/createServerAction';
import { logMessage } from '@/modules/shared/utils';

import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { setQueryClient } from '@/modules/shared/queryClientSingleton';

import { useMessages, useTranslations } from 'next-intl';
import { ReactNode, useRef, useState } from 'react';
import { toast } from 'sonner';
import { messageBus } from '@/modules/shared/messages';
import {
  uploadErrorFromApi,
  uploadErrorMessage,
  type UploadArea,
} from '@/modules/shared/uploadLimits';

interface Props {
  children: ReactNode;
}
export default function QueryProvider({ children }: Props) {
  const t = useTranslations('codes');
  const tValidation = useTranslations('validation');
  const codes = useMessages().codes;

  const tRef = useRef(t);
  const tValidationRef = useRef(tValidation);
  const codesRef = useRef(codes);
  tRef.current = t;
  tValidationRef.current = tValidation;
  codesRef.current = codes;

  function isCodeKey(
    key: string,
    dict: typeof codes
  ): key is keyof typeof dict {
    return key in dict;
  }

  // the client reference should be kept accross rerenders to preserve its state 
  // it should be out the component lifecycle (outside this component) but we need translations so we keep 
  // it inside with using state to keep a perserved reference
  const [queryClient] = useState(() => new QueryClient({
    defaultOptions: {
      queries: {
        retry: (failureCount, error) => {
          // Don't retry on ExpectedError (these are expected/known errors)
          // This includes bad schema (400), unauthorized (401), and forbidden (403) errors
          if (error instanceof ExpectedError) {
            return false;
          }
          // Only retry on unexpected errors, up to 10 times
          return failureCount < 2;
        },
      },
      mutations: {
        retry: (failureCount, error) => {
          // Don't retry on ExpectedError (these are expected/known errors)
          // This includes bad schema (400), unauthorized (401), and forbidden (403) errors
          if (error instanceof ExpectedError) {
            return false;
          }
          // Only retry on unexpected errors, up to 10 times
          return failureCount < 2;
        },
      },
    },

    queryCache: new QueryCache({
      onError: (error, query) => {
        const t = tRef.current;
        const codes = codesRef.current;
        // NOTE: provide meta: {preventGlobalError: true} to useQuery() to disable global error handling for that particular instance
        const meta = query?.meta ?? {};
        if (!('preventGlobalError' in meta && meta.preventGlobalError)) {
          // Handle THREAD_ACCESS_DENIED error - show error and dispatch event to start new chat
          if (error?.message?.includes('THREAD_ACCESS_DENIED')) {
            toast.error(t('THREAD_ACCESS_DENIED'));
            messageBus.publish('thread:access:denied');
            return;
          }

          // Handle 401/403 errors - dispatch event with status code
          // if (error instanceof ExpectedError && (error.statusCode === 401 || error.statusCode === 403)) {
          //   window.dispatchEvent(new CustomEvent('api:auth:error', { detail: { statusCode: error.statusCode } }));
          //   return;
          // }

          //  display expected errors
          if (error instanceof ExpectedError)
            toast.error(
              isCodeKey(error.message, codes) ? t(error.message) : error.message
            );
          // fallback message for unexpected errors
          // error.message here would be "An error occurred in the Server Components render. The specific message is omitted in production builds to avoid leaking sensitive details"
          else {
            logMessage('info', `Query Client : ${error?.message ?? 'error'}`, {
              error,
            });
          
           // toast.error(t('networkError'));
          }
        }
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _, __, mutation) => {
        const t = tRef.current;
        const codes = codesRef.current;
        // logMessage('info', `Mutation Client : ${error.message}`, {
        //   error
        // });

        // NOTE: provide meta: {preventGlobalError: true} to useMutation() to disable
        // global error handling for that instance. preventGlobalError can also be a
        // predicate (error) => boolean to skip only specific errors (e.g. ones the
        // caller handles inline).
        const meta = mutation?.meta ?? {};
        const preventGlobalError =
          'preventGlobalError' in meta
            ? (meta as {
                preventGlobalError?: boolean | ((error: unknown) => boolean);
              }).preventGlobalError
            : undefined;
        const skipGlobalError =
          typeof preventGlobalError === 'function'
            ? preventGlobalError(error)
            : !!preventGlobalError;
        if (!skipGlobalError) {
          // An upload rejected by the server gets the same copy as one caught
          // before sending — the limit quoted depends on where it came from,
          // which the mutation declares as `meta.uploadArea`.
          const uploadArea = (meta as { uploadArea?: UploadArea }).uploadArea;
          const uploadError = uploadArea
            ? uploadErrorFromApi(error)
            : undefined;
          if (uploadArea && uploadError) {
            const { key, values } = uploadErrorMessage(uploadError, uploadArea);
            toast.error(
              tValidationRef.current(
                key as Parameters<typeof tValidationRef.current>[0],
                values
              )
            );
            return;
          }
          //  display expted errors
          if (error instanceof ExpectedError)
            toast.error(
              isCodeKey(error.message, codes) ? t(error.message) : error.message
            );
          // fallback message for unexpected errors
          // error.message here would be "An error occurred in the Server Components render. The specific message is omitted in production builds to avoid leaking sensitive details"
          else {
            logMessage(
              'info',
              `Mutation Client : ${error?.message ?? 'error'}`,
              {
                error,
              }
            );

            

            //toast.error(t('networkError'));
          }
        }
      },
    }),
  }));

  // Expose the shared client to non-React modules (client only).
  if (typeof window !== 'undefined') setQueryClient(queryClient);

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}
